/**
 * Fetches a URL and returns it as a Base64 data URI, or null on any error. Errors intentionally
 * bypass AjaxService's global error channel — one broken inline image shouldn't trigger a
 * user-facing error toast.
 */
const fetchAsDataUri = (url: string): Promise<string | null> => {
  return fetch(url, {
    credentials: 'same-origin',
  })
    .then((response: Response) => {
      if (!response.ok) {
        return null;
      }

      return response.blob();
    })
    .then((blob: Blob | null) => {
      if (blob === null) {
        return null;
      }

      return new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    })
    .catch(() => null);
};

/**
 * Replaces `src` attributes in `html` matching `shouldResolve` with Base64 data URIs. Unresolvable
 * URLs keep their original src. Domain-agnostic by design — callers decide via `shouldResolve`
 * what counts as e.g. an "inline attachment".
 */
const resolveImageSourcesToDataUris = (
  html: string,
  shouldResolve: (url: string) => boolean
): Promise<string> => {
  // Matches quoted src attrs; mirrors the server-side regex in
  // EmailBodyHtmlSanitizer::resolveCidReferences() — keep both in sync.
  const srcPattern = /src\s*=\s*(['"])([^'"]+)\1/gi;

  const urls: string[] = [];
  let match: RegExpExecArray | null;

  while ((match = srcPattern.exec(html)) !== null) {
    const url = match[2];
    if (shouldResolve(url)) {
      urls.push(url);
    }
  }

  if (urls.length === 0) {
    return Promise.resolve(html);
  }

  const uniqueUrls = [...new Set(urls)];

  return Promise.all(
    uniqueUrls.map((url: string) => fetchAsDataUri(url).then((dataUri: string | null) => ({dataUri, url})))
  ).then((results: Array<{dataUri: string | null; url: string}>) => {
    let resolved = html;

    for (const {dataUri, url} of results) {
      if (dataUri === null) {
        continue;
      }

      const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      resolved = resolved.replace(
        new RegExp(`src\\s*=\\s*(['"])${escapedUrl}\\1`, 'g'),
        (fullMatch: string, quote: string) => `src=${quote}${dataUri}${quote}`
      );
    }

    return resolved;
  });
};

export {resolveImageSourcesToDataUris};
